import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Building2, Loader2, Search, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/sonner';
import { FieldError, FieldLabel, TextField } from '@/components/FormField';
import { ApiError } from '@/features/auth/api';
import { normalizePlPostalCode } from './format';
import { isValidEmail, isValidNip, isValidPhone, isValidPlPostalCode, isValidRegon } from './validation';
import {
  useCompanyLookup,
  useCreateCustomer,
  useUpdateCustomer,
  type Customer,
  type CustomerInput,
  type CustomerType,
} from './api';

type Mode = { kind: 'create' } | { kind: 'edit'; customer: Customer };

type FieldKey =
  | 'name'
  | 'nip'
  | 'regon'
  | 'firstName'
  | 'lastName'
  | 'locality'
  | 'buildingNumber'
  | 'postalCode'
  | 'postTown'
  | 'email'
  | 'phone';

type FieldErrors = Partial<Record<FieldKey, string>>;

const FOCUS_ORDER: Array<[FieldKey, string]> = [
  ['nip', 'customer-nip'],
  ['name', 'customer-name'],
  ['regon', 'customer-regon'],
  ['firstName', 'customer-first-name'],
  ['lastName', 'customer-last-name'],
  ['locality', 'customer-locality'],
  ['buildingNumber', 'customer-building'],
  ['postalCode', 'customer-postal'],
  ['postTown', 'customer-town'],
  ['email', 'customer-email'],
  ['phone', 'customer-phone'],
];

function focusFirstError(errors: FieldErrors): void {
  for (const [key, id] of FOCUS_ORDER) {
    if (errors[key]) {
      document.getElementById(id)?.focus();
      return;
    }
  }
}

export function CustomerFormDialog({
  mode,
  children,
  onSaved,
}: {
  mode: Mode;
  children: ReactNode;
  onSaved?: (customer: Customer) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isEdit = mode.kind === 'edit';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="gap-0 p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>
            {isEdit ? t('customers.form.edit_title') : t('customers.form.create_title')}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? t('customers.form.edit_description') : t('customers.form.create_description')}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="cn-dialog-scroll">
          <div className="px-6 pb-6">
            <CustomerForm
              mode={mode}
              onDone={(customer) => {
                setOpen(false);
                onSaved?.(customer);
              }}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function CustomerForm({ mode, onDone }: { mode: Mode; onDone: (customer: Customer) => void }) {
  const { t } = useTranslation();
  const isEdit = mode.kind === 'edit';
  const initial = mode.kind === 'edit' ? mode.customer : null;

  const [type, setType] = useState<CustomerType | null>(initial?.type ?? null);
  const [name, setName] = useState(initial?.type === 'COMPANY' ? initial.name : '');
  const [nip, setNip] = useState(initial?.nip ?? '');
  const [regon, setRegon] = useState(initial?.regon ?? '');
  const [firstName, setFirstName] = useState(initial?.firstName ?? '');
  const [lastName, setLastName] = useState(initial?.lastName ?? '');
  const [locality, setLocality] = useState(initial?.address.locality ?? '');
  const [street, setStreet] = useState(initial?.address.street ?? '');
  const [buildingNumber, setBuildingNumber] = useState(initial?.address.buildingNumber ?? '');
  const [unitNumber, setUnitNumber] = useState(initial?.address.unitNumber ?? '');
  const [postalCode, setPostalCode] = useState(initial?.address.postalCode ?? '');
  const [postTown, setPostTown] = useState(initial?.address.postTown ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [showErrors, setShowErrors] = useState(false);
  const [nipTaken, setNipTaken] = useState(false);

  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const lookup = useCompanyLookup();
  const pending = create.isPending || update.isPending;

  if (!isEdit && type === null) {
    return <TypeChooser onChoose={setType} />;
  }

  const effectiveType: CustomerType = type ?? 'COMPANY';

  const computeErrors = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (effectiveType === 'COMPANY') {
      if (name.trim() === '') errors.name = 'customers.form.validation.name_required';
      if (nip.trim() === '') errors.nip = 'customers.form.validation.nip_required';
      else if (!isValidNip(nip)) errors.nip = 'customers.form.validation.nip_invalid';
      if (regon.trim() !== '' && !isValidRegon(regon))
        errors.regon = 'customers.form.validation.regon_invalid';
    } else {
      if (firstName.trim() === '')
        errors.firstName = 'customers.form.validation.first_name_required';
      if (lastName.trim() === '') errors.lastName = 'customers.form.validation.last_name_required';
    }
    if (locality.trim() === '') errors.locality = 'customers.form.validation.locality_required';
    if (buildingNumber.trim() === '')
      errors.buildingNumber = 'customers.form.validation.building_required';
    if (postalCode.trim() === '') errors.postalCode = 'customers.form.validation.postal_required';
    else if (!isValidPlPostalCode(postalCode))
      errors.postalCode = 'customers.form.validation.postal_invalid';
    if (postTown.trim() === '') errors.postTown = 'customers.form.validation.post_town_required';
    if (email.trim() !== '' && !isValidEmail(email))
      errors.email = 'customers.form.validation.email_invalid';
    if (phone.trim() !== '' && !isValidPhone(phone))
      errors.phone = 'customers.form.validation.phone_invalid';
    return errors;
  };

  const errors: FieldErrors = showErrors ? computeErrors() : {};
  if (nipTaken) errors.nip = errors.nip ?? 'customers.form.errors.duplicate_nip';

  const runLookup = () => {
    const value = nip.trim();
    if (value === '') return;
    lookup.mutate(value, {
      onSuccess: (company) => {
        setName(company.name);
        setNip(company.nip);
        setRegon(company.regon ?? '');
        if (company.address) {
          setLocality(company.address.locality);
          setStreet(company.address.street ?? '');
          setBuildingNumber(company.address.buildingNumber);
          setUnitNumber(company.address.unitNumber ?? '');
          setPostalCode(company.address.postalCode);
          setPostTown(company.address.postTown);
        }
      },
    });
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (pending) return;
    setNipTaken(false);
    const found = computeErrors();
    if (Object.keys(found).length > 0) {
      setShowErrors(true);
      focusFirstError(found);
      return;
    }
    setShowErrors(false);
    const address = {
      locality: locality.trim(),
      street: blankToNull(street),
      buildingNumber: buildingNumber.trim(),
      unitNumber: blankToNull(unitNumber),
      postalCode: normalizePlPostalCode(postalCode),
      postTown: postTown.trim(),
      countryCode: 'PL',
    };
    const input: CustomerInput =
      effectiveType === 'COMPANY'
        ? {
            type: 'COMPANY',
            name: name.trim(),
            firstName: null,
            lastName: null,
            nip: blankToNull(nip),
            regon: blankToNull(regon),
            address,
            email: blankToNull(email),
            phone: blankToNull(phone),
          }
        : {
            type: 'PRIVATE_PERSON',
            name: null,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            nip: null,
            regon: null,
            address,
            email: blankToNull(email),
            phone: blankToNull(phone),
          };
    const onError = (error: ApiError) => {
      if (error.status === 409) {
        setNipTaken(true);
        document.getElementById('customer-nip')?.focus();
        return;
      }
      toast.error(t(submitErrorKey(error)));
    };
    if (mode.kind === 'edit') {
      update.mutate(
        { id: mode.customer.id, input },
        {
          onSuccess: (customer) => {
            toast.success(t('customers.toasts.updated'));
            onDone(customer);
          },
          onError,
        },
      );
    } else {
      create.mutate(input, {
        onSuccess: (customer) => {
          toast.success(t('customers.toasts.created'));
          onDone(customer);
        },
        onError,
      });
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      {!isEdit ? (
        <Button
          type="button"
          variant="ghost"
          className="-ml-2 text-muted-foreground"
          onClick={() => setType(null)}
        >
          <ArrowLeft data-icon="inline-start" />
          {t('customers.form.type.change')}
        </Button>
      ) : null}

      {effectiveType === 'COMPANY' ? (
        <>
          <div className="space-y-1.5">
            <FieldLabel htmlFor="customer-nip" required>
              {t('customers.form.fields.nip')}
            </FieldLabel>
            <div className="flex gap-2">
              <Input
                id="customer-nip"
                inputMode="numeric"
                autoComplete="off"
                value={nip}
                onChange={(e) => {
                  setNip(e.target.value);
                  setNipTaken(false);
                }}
                aria-required
                aria-invalid={errors.nip ? true : undefined}
                aria-describedby={errors.nip ? 'customer-nip-error' : undefined}
              />
              {!isEdit ? (
                <Button
                  type="button"
                  variant="outline"
                  className="whitespace-nowrap"
                  disabled={lookup.isPending || nip.trim() === ''}
                  onClick={runLookup}
                >
                  {lookup.isPending ? (
                    <Loader2 data-icon="inline-start" className="animate-spin" />
                  ) : (
                    <Search data-icon="inline-start" />
                  )}
                  {t('customers.form.lookup')}
                </Button>
              ) : null}
            </div>
            {errors.nip ? (
              <FieldError id="customer-nip-error" message={t(errors.nip)} />
            ) : !isEdit ? (
              lookup.isError ? (
                <p className="text-xs text-destructive">{t(lookupErrorKey(lookup.error))}</p>
              ) : (
                <p className="text-xs text-muted-foreground">{t('customers.form.lookup_hint')}</p>
              )
            ) : null}
          </div>

          <TextField
            id="customer-name"
            label={t('customers.form.fields.name')}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={errors.name ? t(errors.name) : undefined}
          />

          <TextField
            id="customer-regon"
            label={t('customers.form.fields.regon')}
            inputMode="numeric"
            value={regon}
            onChange={(e) => setRegon(e.target.value)}
            error={errors.regon ? t(errors.regon) : undefined}
          />
        </>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            id="customer-first-name"
            label={t('customers.form.fields.first_name')}
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={errors.firstName ? t(errors.firstName) : undefined}
          />
          <TextField
            id="customer-last-name"
            label={t('customers.form.fields.last_name')}
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={errors.lastName ? t(errors.lastName) : undefined}
          />
        </div>
      )}

      <Separator />

      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          id="customer-locality"
          className="sm:col-span-2"
          label={t('customers.form.fields.locality')}
          required
          value={locality}
          onChange={(e) => setLocality(e.target.value)}
          error={errors.locality ? t(errors.locality) : undefined}
        />
        <TextField
          id="customer-street"
          className="sm:col-span-2"
          label={t('customers.form.fields.street')}
          value={street}
          onChange={(e) => setStreet(e.target.value)}
        />
        <TextField
          id="customer-building"
          label={t('customers.form.fields.building_number')}
          required
          value={buildingNumber}
          onChange={(e) => setBuildingNumber(e.target.value)}
          error={errors.buildingNumber ? t(errors.buildingNumber) : undefined}
        />
        <TextField
          id="customer-unit"
          label={t('customers.form.fields.unit_number')}
          value={unitNumber}
          onChange={(e) => setUnitNumber(e.target.value)}
        />
        <TextField
          id="customer-postal"
          label={t('customers.form.fields.postal_code')}
          required
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          error={errors.postalCode ? t(errors.postalCode) : undefined}
        />
        <TextField
          id="customer-town"
          label={t('customers.form.fields.post_town')}
          required
          value={postTown}
          onChange={(e) => setPostTown(e.target.value)}
          error={errors.postTown ? t(errors.postTown) : undefined}
        />
      </div>

      <Separator />

      <div className="grid gap-3 sm:grid-cols-2">
        <TextField
          id="customer-email"
          label={t('customers.form.fields.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email ? t(errors.email) : undefined}
        />
        <TextField
          id="customer-phone"
          label={t('customers.form.fields.phone')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={errors.phone ? t(errors.phone) : undefined}
        />
      </div>

      <Button type="submit" className="w-full" disabled={pending}>
        {pending ? <Loader2 className="animate-spin" /> : null}
        {isEdit ? t('customers.form.save') : t('customers.form.add')}
      </Button>
    </form>
  );
}

function TypeChooser({ onChoose }: { onChoose: (type: CustomerType) => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t('customers.form.type.prompt')}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <TypeOption
          icon={<Building2 className="size-5" />}
          label={t('customers.form.type.company')}
          hint={t('customers.form.type.company_hint')}
          onClick={() => onChoose('COMPANY')}
        />
        <TypeOption
          icon={<User className="size-5" />}
          label={t('customers.form.type.person')}
          hint={t('customers.form.type.person_hint')}
          onClick={() => onChoose('PRIVATE_PERSON')}
        />
      </div>
    </div>
  );
}

function TypeOption({
  icon,
  label,
  hint,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors hover:bg-muted"
    >
      <span className="mb-1 flex items-center gap-2">
        <span className="flex size-9 items-center justify-center rounded-md bg-muted text-foreground">
          {icon}
        </span>
        <span className="text-sm font-medium">{label}</span>
      </span>
      <span className="text-xs text-muted-foreground">{hint}</span>
    </button>
  );
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function lookupErrorKey(error: ApiError): string {
  switch (error.status) {
    case 400:
      return 'customers.errors.invalid_nip';
    case 404:
      return 'customers.errors.not_found';
    case 429:
      return 'customers.errors.rate_limited';
    default:
      return 'customers.errors.failed';
  }
}

function submitErrorKey(error: ApiError): string {
  switch (error.status) {
    case 409:
      return 'customers.form.errors.duplicate_nip';
    case 400:
      return 'customers.form.errors.invalid';
    default:
      return 'customers.form.errors.failed';
  }
}
